import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Database } from '@/database.types'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { Search, ArrowUpDown, LayoutGrid, Rows, Grid3X3 } from 'lucide-react'
import { NewsContentDialog } from './NewsContentDialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
} from '@tanstack/react-table'

type NewsContent = Database['public']['Tables']['news_contents']['Row']

type ViewMode = 'table' | 'tiles' | 'supertile'

const VIEW_MODE_STORAGE_KEY = 'news-contents-view-mode'

const extractPrimaryImage = (images: NewsContent['images']): string | null => {
    if (!images) return null

    try {
        const parsed = images as unknown

        if (Array.isArray(parsed)) {
            for (const item of parsed) {
                if (!item) continue

                if (typeof item === 'string') {
                    return item
                }

                if (typeof item === 'object') {
                    const candidate =
                        (item as Record<string, unknown>).url ??
                        (item as Record<string, unknown>).image_url ??
                        (item as Record<string, unknown>).src

                    if (typeof candidate === 'string') {
                        return candidate
                    }
                }
            }
        }

        if (typeof parsed === 'object' && parsed !== null) {
            const candidate =
                (parsed as Record<string, unknown>).url ??
                (parsed as Record<string, unknown>).image_url ??
                (parsed as Record<string, unknown>).src

            if (typeof candidate === 'string') {
                return candidate
            }
        }
    } catch (error) {
        console.debug('Unable to parse news content images', error)
    }

    return null
}

const getContentPreview = (content: NewsContent): string => {
    const source = content.raw_content || content.description || ''
    if (!source) return ''

    const normalized = source.replace(/\s+/g, ' ').trim()
    if (normalized.length <= 160) {
        return normalized
    }

    return `${normalized.slice(0, 157)}...`
}

const formatDisplayDate = (value: string) => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return ''
    }

    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

export default function NewsContentsView() {
    const [contents, setContents] = useState<NewsContent[]>([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(0)
    const [searchTerm, setSearchTerm] = useState('')
    const [totalCount, setTotalCount] = useState(0)
    const [sorting, setSorting] = useState<SortingState>([])
    const [selectedContent, setSelectedContent] = useState<NewsContent | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        if (typeof window === 'undefined') {
            return 'table'
        }

        const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY)
        return stored === 'tiles' || stored === 'supertile' ? (stored as ViewMode) : 'table'
    })
    const { toast } = useToast()
    const pageSize = viewMode === 'supertile' ? 100 : 10

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode)
    }, [viewMode])

    const columns: ColumnDef<NewsContent>[] = [
        {
            accessorKey: "raw_headline",
            header: ({ column }) => {
                return (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Title
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                )
            },
            cell: ({ row }) => {
                const title = (row.getValue("raw_headline") as string) || row.original.name
                return <div className="font-medium">{title}</div>
            },
        },
        {
            accessorKey: "raw_content",
            header: "Content",
            cell: ({ row }) => {
                const content = (row.getValue("raw_content") as string | null) || row.original.description
                return (
                    <div className="max-w-[500px] truncate">
                        {content || ""}
                    </div>
                )
            },
        },
        {
            accessorKey: "created_at",
            header: ({ column }) => {
                return (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Date
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                )
            },
            cell: ({ row }) => {
                return new Date(row.getValue("created_at")).toLocaleDateString()
            },
        }
    ]

    const fetchContents = async () => {
        try {
            setLoading(true)
            let query = supabase
                .from('news_contents')
                .select('*', { count: 'exact' })
                .range(page * pageSize, (page + 1) * pageSize - 1)

            if (searchTerm) {
                query = query.or(`raw_headline.ilike.%${searchTerm}%,raw_content.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%`)
            }

            if (sorting.length > 0) {
                const { id, desc } = sorting[0]
                query = query.order(id, { ascending: !desc })
            } else {
                query = query.order('created_at', { ascending: false })
            }

            const { data, error, count } = await query

            if (error) throw error

            setContents(data || [])
            setTotalCount(count || 0)
        } catch (error) {
            console.error('Error fetching news contents:', error)
            toast({
                title: "Error fetching news contents",
                description: "There was a problem loading the news contents.",
                variant: "destructive",
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchContents()
    }, [page, searchTerm, sorting, viewMode])

    const table = useReactTable({
        data: contents,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
        },
        manualSorting: true,
        pageCount: Math.ceil(totalCount / pageSize),
    })

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        setPage(0)
        fetchContents()
    }

    const handleViewModeChange = (mode: ViewMode) => {
        setViewMode(mode)
        setPage(0)
    }

    const loader = (
        <div className="flex h-48 items-center justify-center">
            <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
    )

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between md:items-center">
                <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-xl font-bold">News Contents</h2>
                    <div className="flex items-center gap-1 rounded-lg border bg-background p-1">
                        <Button
                            type="button"
                            size="sm"
                            variant={viewMode === 'table' ? 'default' : 'ghost'}
                            className="h-9 gap-2 px-3"
                            onClick={() => handleViewModeChange('table')}
                        >
                            <Rows className="h-4 w-4" />
                            <span className="hidden sm:inline">Table</span>
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={viewMode === 'tiles' ? 'default' : 'ghost'}
                            className="h-9 gap-2 px-3"
                            onClick={() => handleViewModeChange('tiles')}
                        >
                            <LayoutGrid className="h-4 w-4" />
                            <span className="hidden sm:inline">Tiles</span>
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={viewMode === 'supertile' ? 'default' : 'ghost'}
                            className="h-9 gap-2 px-3"
                            onClick={() => handleViewModeChange('supertile')}
                        >
                            <Grid3X3 className="h-4 w-4" />
                            <span className="hidden sm:inline">Supertile</span>
                        </Button>
                    </div>
                </div>
                <form onSubmit={handleSearch} className="flex w-full gap-2 sm:w-auto">
                    <Input
                        type="text"
                        placeholder="Search headlines and content..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full sm:w-64"
                    />
                    <Button type="submit" size="icon" className="shrink-0">
                        <Search className="h-4 w-4" />
                    </Button>
                </form>
            </div>

            {viewMode === 'table' ? (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        <div className="flex justify-center">
                                            <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() => {
                                            setSelectedContent(row.original)
                                            setDialogOpen(true)
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        No results found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            ) : viewMode === 'tiles' ? (
                <div className="rounded-md border p-4">
                    {loading ? (
                        loader
                    ) : contents.length ? (
                        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                            {contents.map((content) => {
                                const headline = content.raw_headline || content.name
                                const imageUrl = extractPrimaryImage(content.images)
                                const preview = getContentPreview(content)
                                const createdAt = formatDisplayDate(content.created_at)
                                const fallbackInitial = headline?.trim().charAt(0)?.toUpperCase() || 'N'

                                return (
                                    <button
                                        key={content.id}
                                        type="button"
                                        onClick={() => {
                                            setSelectedContent(content)
                                            setDialogOpen(true)
                                        }}
                                        className="group flex h-full flex-col overflow-hidden rounded-lg border bg-background text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                    >
                                        {imageUrl ? (
                                            <div className="aspect-video overflow-hidden bg-muted">
                                                <img
                                                    src={imageUrl}
                                                    alt={headline || 'News content image'}
                                                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                                    onError={(event) => {
                                                        event.currentTarget.style.display = 'none'
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-primary/10 via-primary/5 to-primary/20 text-3xl font-semibold text-primary">
                                                <span aria-hidden="true">{fallbackInitial}</span>
                                            </div>
                                        )}
                                        <div className="flex flex-1 flex-col gap-2 p-4">
                                            <h3 className="line-clamp-2 text-base font-semibold text-foreground">{headline}</h3>
                                            {preview && (
                                                <p className="line-clamp-3 text-sm text-muted-foreground">{preview}</p>
                                            )}
                                            <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
                                                <span>{createdAt}</span>
                                                {content.status && (
                                                    <span className="rounded-full bg-muted px-2 py-0.5 font-medium uppercase tracking-wide text-[10px]">
                                                        {content.status}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                            No results found
                        </div>
                    )}
                </div>
            ) : (
                <div className="rounded-md border p-3">
                    {loading ? (
                        loader
                    ) : contents.length ? (
                        <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-8 2xl:grid-cols-10">
                            {contents.map((content) => {
                                const headline = content.raw_headline || content.name
                                const imageUrl = extractPrimaryImage(content.images)
                                const fallbackInitial = headline?.trim().charAt(0)?.toUpperCase() || 'N'

                                return (
                                    <button
                                        key={content.id}
                                        type="button"
                                        onClick={() => {
                                            setSelectedContent(content)
                                            setDialogOpen(true)
                                        }}
                                        className="group flex h-full flex-col overflow-hidden rounded-md border bg-background text-left transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                    >
                                        <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt={headline || 'News content image'}
                                                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                                    onError={(event) => {
                                                        event.currentTarget.style.display = 'none'
                                                    }}
                                                />
                                            ) : (
                                                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/10 via-primary/5 to-primary/20 text-lg font-semibold text-primary">
                                                    <span aria-hidden="true">{fallbackInitial}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-2">
                                            <h3 className="line-clamp-2 text-xs font-semibold text-foreground">
                                                {headline}
                                            </h3>
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                            No results found
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                    {totalCount > 0
                        ? `Showing ${page * pageSize + 1}-${Math.min((page + 1) * pageSize, totalCount)} of ${totalCount} results`
                        : 'No results'}
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                    >
                        Previous
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * pageSize >= totalCount}
                    >
                        Next
                    </Button>
                </div>
            </div>

            <NewsContentDialog
                content={selectedContent}
                open={dialogOpen}
                onOpenChange={setDialogOpen}
            />
        </div>
    )
} 